// AmountChip — MD3 Chip с числом и валютой. Currency-aware formatting (RUB/USD/EUR/CNY).
import { Chip } from '@mui/material';
import type { DealCurrency } from '@/api/deals';

/** Сопоставление валюты → символ для отображения. */
const CURRENCY_SYMBOLS: Record<DealCurrency, string> = {
  RUB: '₽',
  USD: '$',
  EUR: '€',
  CNY: '¥',
};

/** Сопоставление валюты → ISO-код (для случаев, когда символ неоднозначен). */
const CURRENCY_CODES: Record<DealCurrency, string> = {
  RUB: 'RUB',
  USD: 'USD',
  EUR: 'EUR',
  CNY: 'CNY',
};

/** Форматирование суммы: Intl.NumberFormat с ru-RU locale. */
function formatAmount(amount: number, currency: DealCurrency): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  // Используем Intl.NumberFormat для группировки разрядов (1 000 000).
  const formatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
  return `${formatter.format(amount)} ${symbol}`;
}

interface AmountChipProps {
  /** Сумма (или null если не указана). */
  amount: number | null;
  /** Валюта. */
  currency: DealCurrency;
  /** Размер чипа. */
  size?: 'small' | 'medium';
}

/** MD3 Chip для отображения суммы сделки в указанной валюте. */
export function AmountChip({ amount, currency, size = 'small' }: AmountChipProps): JSX.Element {
  const label = amount === null ? '—' : formatAmount(amount, currency);
  return (
    <Chip
      data-testid="amount-chip"
      label={label}
      size={size}
      variant="outlined"
      title={amount === null ? 'Сумма не указана' : `${CURRENCY_CODES[currency]} ${amount}`}
    />
  );
}
